import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import noop from 'lodash/noop';

import DraggableArea from './DraggableArea';
import { ConnectedUpdateCouponButton as UpdateCouponButton } from './UpdateCouponButton';
import Big9Spil from '../../../components/big9/Big9Spil';
import ChangeBetPrice from 'features/Betslip/ChangeBetPrice';
import BetslipHeader from '../../Betslip/BetslipHeader';
import Text from 'ui/Text';
import { PRICES } from '../../Betslip';

import {
    changeCombinationPrice,
    clearMarks,
    setCouponData,
    setWildcard,
} from 'components/big9/actions';

import { convertCouponToBig9Format } from '../state/helpers';
import { formatDenmarkCurrency } from 'utils/UiHelper';

import {
    combinationCount,
    combinationPrice,
} from '../../../components/big9/selectors';

import '../styles/edit-coupon.css';

const Header = ({ closeEditing }) => (
    <div className="flex container-md align-center space-between no-bottom-padding">
        <span className="sk-title">Ret kupon til SpilKlub 25</span>
        <div className="sk-close-btn-icon" onClick={closeEditing}>
            <i className="fa fa-times" aria-hidden="true" />
        </div>
    </div>
);

const MaxBetPriceWarning = ({ maxBetPrice, shown }) => {
    const cls = classNames('sk-edit-coupon-max-price-warning fade-in', {
        hidden: !shown,
    });

    return (
        <div className={cls}>
            <Text condensed size="x1" color="#fff" padding="0" align="center">
                Max indsats er {formatDenmarkCurrency(maxBetPrice)} kr.
            </Text>
        </div>
    );
};

const MAX_BET_PRICE = 60000;

const EditCoupon = ({
    coupon: originalCoupon,
    originalWildcardEventNumber,
    poolHash,
    setCouponData,
    clearCouponData,
    setWildcard,
    changeCombinationPrice,
    updatedCombinationPrice,
    updatedCombinationCount,
    shown = false,
    closeEditor = noop,
    refetchPoolDetails,
}) => {
    const [prefilled, setPrefilled] = useState(false);

    const [pricePopupShown, setPricePopupVisibility] = useState(false);

    const changePrice = (price) => {
        changeCombinationPrice(price);
        setPricePopupVisibility(false);
    };

    const originalSelections = convertCouponToBig9Format(originalCoupon);

    const bottomBarCls = classNames('sk-bottom-bar', {
        'sk-betslip-price-form-collapsed': !pricePopupShown,
    });

    const maxBetPriceExceeded =
        updatedCombinationPrice * updatedCombinationCount > MAX_BET_PRICE;

    // prefilling the coupon selections
    useEffect(
        () => {
            if (shown) {
                clearCouponData();
                // sometimes previously opened coupon selections appear on editor popup
                // this setTimeout wrapper reschedules setCouponData so it fixes that issue
                setTimeout(() => {
                    setCouponData(originalSelections);
                    setWildcard(originalWildcardEventNumber);
                    // setPrefilled is needed to prevent selected buttons 'flickering' right after prefilling
                    // so the editor popup will be opened right after 'prefilled' state is changed
                    setPrefilled(true);
                }, 0);
            } else {
                clearCouponData();
                setPrefilled(false);
            }
        },
        [shown]
    );

    return (
        <>
            <DraggableArea
                shown={shown && prefilled}
                onOverlayClick={closeEditor}
                className="edit-coupon-popup"
            >
                <Header closeEditing={closeEditor} />

                {shown && (
                    <Big9Spil innerComponentMode hideHeader hideTurnover />
                )}
            </DraggableArea>

            {maxBetPriceExceeded && (
                <MaxBetPriceWarning maxBetPrice={MAX_BET_PRICE} shown={shown} />
            )}

            <UpdateCouponButton
                refetchPoolDetails={refetchPoolDetails}
                shown={shown && prefilled}
                disabled={
                    maxBetPriceExceeded /*||
                    updatedCombinationPrice * updatedCombinationCount < 8*/
                }
                originalSelections={originalSelections}
                originalCoupon={originalCoupon}
                closeEditor={closeEditor}
                poolHash={poolHash}
            />

            <div className={bottomBarCls}>
                {shown && (
                    <BetslipHeader
                        className="fade-in"
                        openPriceHandler={() => setPricePopupVisibility(true)}
                        combinationCount={updatedCombinationCount}
                        combinationPrice={updatedCombinationPrice}
                        innerComponentMode
                        fullOpen
                    />
                )}
                <ChangeBetPrice
                    className="border-box"
                    prices={PRICES}
                    combinationPrice={combinationPrice}
                    changePrice={changePrice}
                />
            </div>
        </>
    );
};

const stateToProps = (state) => ({
    updatedCombinationPrice: combinationPrice(state),
    updatedCombinationCount: combinationCount(state),
});

const dispatchToProps = (dispatch) => ({
    setCouponData: (couponData) => dispatch(setCouponData(couponData)),
    clearCouponData: () => dispatch(clearMarks()),
    setWildcard: (wildcard) => dispatch(setWildcard(wildcard)),
    changeCombinationPrice: (price) => {
        dispatch(changeCombinationPrice(price));
    },
});

export const ConnectedEditCoupon = connect(
    stateToProps,
    dispatchToProps
)(EditCoupon);

export default EditCoupon;
